import { useCallback, useEffect, useState } from "react";
import { Upload } from "antd";
import UploadFile from "../assets/icons/upload-file.svg";
import axios from "axios";
import { ReactComponent as DeleteIcon } from "../assets/icons/trash.svg";

import { useUserProvider } from "../providers/UserProvider";
import { useLoaderProvider } from "../providers/LoaderProvider";

const { Dragger } = Upload;

const mimeTypeToType = {
  // Image types
  "image/jpeg": "image",
  "image/png": "image",
  "image/gif": "image",
  "image/bmp": "image",
  "image/webp": "image",
  "image/svg+xml": "image",
  "image/jpg": "image",
  "image/tiff": "image",
  "image/vnd.adobe.photoshop": "image",
  "application/postscript": "file",
  "application/x-indesign": "file",
  "image/x-sony-arw": "image",
  "image/x-canon-cr2": "image",
  "image/x-nikon-nef": "image",
  "image/x-olympus-orf": "image",
  "image/x-panasonic-raw": "image",
  "image/x-adobe-dng": "image",

  // Document types
  "application/pdf": "file",
  "application/msword": "file",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "file",
  "application/vnd.ms-excel": "file",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "file",
  "application/vnd.ms-powerpoint": "file",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "file",
  "text/plain": "file",
  "text/csv": "file",
  "application/rtf": "file",

  // Archive types
  "application/zip": "file",
  "application/x-rar-compressed": "file",
  "application/x-tar": "file",
  "application/gzip": "file",
  "application/x-7z-compressed": "file",
};

const getTypeFromMime = (mimeType) => {
  return mimeTypeToType[mimeType] || "unknown"; // Default to 'unknown' if MIME type isn't mapped
};

const formatFileSize = (sizeInBytes) => {
  const GB = 1_073_741_824; // 1 GB in bytes
  const MB = 1_048_576;    // 1 MB in bytes

  if (sizeInBytes < GB) {
    return `${(sizeInBytes / MB).toFixed(0)} MB`;
  } else {
    return `${(sizeInBytes / GB).toFixed(2)} GB`;
  }
};

export const FileUpload = ({
  fileType,
  isButton = false,
  children,
  onChange,
  acceptedTypes,
  errorMessage,
  onError,
  error,
  multiple = false,
  value,
  metaData,
  maximumFileLength,
  successCallback,
  onRemove,
  disabled,
  maximumFileSize = 10000000,
  uploadLabelModifier,
}) => {
  const fileUploadName = "file";
  const [file, setFile] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useUserProvider();
  const { doSetShowLoadingModal } = useLoaderProvider();
  const [uploadQueue, setUploadQueue] = useState([]);

  const handleDefaultFile = useCallback(() => {
    if (value && multiple) {
      setFileList(value);
    }

    if (value && !multiple) {
      setFile(value);
    }
  }, [value]);

  useEffect(() => {
    handleDefaultFile();
  }, []);

  useEffect(() => {
    if (!value) return;

    if (multiple && value?.length > 0) {
      return setFileList(
        value?.map((a) => {
          const uuid = a?.match(
            /[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i
          )[0];

          return {
            id: metaData[uuid]?.id,
            name: metaData[uuid]?.filename,
          };
        })
      );
    }

    if (
      value &&
      /^media:[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i.test(
        value
      )
    ) {
      const uuid = value?.match(
        /[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i
      )[0];

      if (!file?.id && !!uuid && metaData && metaData?.[uuid]) {
        setFile({
          id: metaData[uuid]?.id,
          name: metaData[uuid]?.filename,
        });
      }
    }
  }, [value, metaData, multiple]);

  useEffect(() => {
    doSetShowLoadingModal(loading);
  }, [loading]);

  useEffect(() => {
    if (uploadQueue.length > 0) {
      processUploadQueue(uploadQueue);
    }
  }, [uploadQueue]);

  const processUploadQueue = async (queue) => {
    setLoading(true);
    const tempFileList = [...fileList];
    const metaData = [];
    for (const file of queue) {
      try {
        const type = file.type;
        const category = getTypeFromMime(type);

        const formData = new FormData();
        formData.append("type", category);
        formData.append("file", file);

        const response = await axios.post(
          `${process.env.REACT_APP_API}/media`,
          formData,
          {
            headers: {
              "content-type": "multipart/form-data",
              Authorization: "Bearer " + user.idToken,
            },
          }
        );

        const data = response.data.data;
        if (multiple) {
          metaData.push(data);
          tempFileList.push({ name: file.name, id: data.id });
          successCallback?.(tempFileList);
        } else {
          setFile({ name: file.name, id: data.id });
          onChange?.("media:" + data.id, data);
          successCallback?.("media:" + data.id);
        }
      } catch (error) {
        console.error("Upload failed:", error);
        onError("Upload failed");
      }
    }

    setFileList(tempFileList);
    onError('')
    if (multiple) {
      onChange?.(
        tempFileList.map((file) => "media:" + file.id),
        metaData
      );
    }
    setLoading(false);
    setUploadQueue([]);
  };

  const handleUpload = async (fileList) => {
    const filesToUpload = fileList;
    if (filesToUpload.length > 0) {
      setUploadQueue(filesToUpload);
    }
  };

  const draggerProps = {
    accept: acceptedTypes,
    name: "file",
    multiple: multiple,
    beforeUpload: (_, list) => {
      const lengthAfterUpload = list?.length + fileList.length;
      let filesToUpload = [];
      let hasError = false
      if (lengthAfterUpload > maximumFileLength) {
        onError(`You can only upload ${maximumFileLength} files.`);
        return false;
      }

      for (const file of list) {
        const type = file.type || file?.name?.split(".")?.pop();
        
        if (file.size > maximumFileSize) {
          onError(`File size should not exceed ${formatFileSize(maximumFileSize)}`);
          hasError = true
          return false; // Stop further processing if there's an error
        }
    
        if (!acceptedTypes.includes(type) && fileType !== "*" && fileType) {
          onError(errorMessage ?? "Invalid File Type");
          hasError = true
          return false; // Stop further processing if there's an error
        }
    
        filesToUpload.push(file);
      }
      
      if(hasError){
        setLoading(false)
        return false
      }
      handleUpload(filesToUpload);
      return false; // Prevents automatic upload
    },
    showUploadList: false,
  };

  const handleDownloadFile = async (id) => {
    const path = metaData[id]?.url;
    const filename = metaData[id]?.filename;
    const response = await fetch(path);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div key={`file-upload-${value || "1"}`}>
      {isButton ? (
        <Upload
          fileList={fileList}
          name={fileUploadName}
          maxCount={1}
          accept={`${fileType || "text/zip"}`}
          onRemove={() => {
            setFile(null);
            if (typeof onRemove === "function") onRemove();
          }}
          disabled={disabled}
        >
          <button
            className="flex justify-center items-center border-4 border-aeblue border-solid gap-0.5 text-sm py-1.5 px-5 !bg-none rounded-lg uppercase text-aeblue-default font-bold"
            type="button"
            onClick={(e) => {
              e.preventDefault();
            }}
          >
            <p className="ml-1 file-upload-btn">Add Item</p>
          </button>
        </Upload>
      ) : (
        <Dragger
          fileList={fileList}
          name={fileUploadName}
          disabled={disabled}
          maxCount={1}
          accept={`${fileType || "text/zip"}`}
          onRemove={() => {
            setFile(null);
            if (typeof onRemove === "function") onRemove();
          }}
          className="!p-0"
          {...draggerProps}
        >
          {children ? (
            children
          ) : (
            <>
              <p className="justify-center w-full flex">
                <img src={UploadFile} alt="Upload File" />
              </p>
              <p className="text-lg py-5">
                Drag and drop files here or click to browse your files
              </p>
            </>
          )}
        </Dragger>
      )}
      {multiple ? (
        <>
          {fileList.length > 0 && (
            <div>
              {fileList?.map((file) => (
                <li className="mt-2 flex" key={file.id}>
                  <button
                    type="button"
                    className="ml-2"
                    onClick={() => {
                      onChange?.(value.filter((a) => a !== "media:" + file.id));
                      setFileList(fileList.filter((f) => f.id !== file.id));
                    }}
                    disabled={disabled}
                  >
                    <DeleteIcon className="mr-2 h-5 w-5 fill-current text-gray-400 trash hover:text-red-600 " />
                  </button>
                  <div
                    className={`cursor-pointer text-aeblue-link underline hover:no-underline ${uploadLabelModifier ?? ''}`}
                    onClick={() => {
                      handleDownloadFile(file.id);
                    }}
                  >
                    {file?.name}
                  </div>
                </li>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
        {file && (
          <li className="mt-2 flex">
            <button
              type="button"
              className="ml-2"
              onClick={() => {
                setFile(null);
                onChange?.("");
                if (typeof onRemove === "function") onRemove();
              }}
              disabled={disabled}
            >
              <DeleteIcon className="mr-2 h-5 w-5 fill-current text-gray-400 trash hover:text-red-600" />
            </button>
            <div
              className={`cursor-pointer text-aeblue-link underline hover:no-underline ${uploadLabelModifier ?? ''}`}
              onClick={() => {
                handleDownloadFile(file.id);
              }}
            >
              {file?.name}
            </div>
          </li>
        )}
      </>
    )}

      {error && <div className="text-red-600 mt-1">{error}</div>}
    </div>
  );
};
